import React, { useState, useEffect } from "react";
import { IVWPost } from "@/interfaces";
import {
  useTheme,
  Table,
  TableCell,
  TableRow,
  Typography,
  Box,
  Avatar,
  Grid,
  TableHead,
  TableBody,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import TablePagination from "@/components/Table/TablePagination";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/Modal";
import { UserStore } from "@/store/user";
import { enqueueSnackbar } from "notistack";
import { autorun } from "mobx";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import { formatedBigNumber, stringAvatar } from "@/utility";
import BigNumber from "bignumber.js";
import numeral from "numeral";

interface ITradeTableProps {
  tradeList?: IVWPost[];
  userStore?: UserStore;
  setSelectedPost: (newPost: IVWPost) => void;
}

const TradeTable = inject(Store.type.userStore)(
  observer(({ tradeList, userStore, setSelectedPost }: ITradeTableProps) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [isLogin, setIsLogin] = useState<boolean | undefined>(undefined);
    const [selectStore, setSelectStore] = useState<IVWPost | null>(null);

    useEffect(() => {
      const disposeAutorun = autorun(() => {
        setIsLogin(userStore?.isLogin);
      });
      return () => disposeAutorun();
    }, [userStore]);

    const [page, setPage] = useState(1);
    const rowsPerPage = 12;
    const handleChangePage = (newPage: number) => {
      setPage(newPage);
    };

    return (
      <>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "11.15%" }}>Asset</TableCell>
              <TableCell style={{ width: "18.85%" }}>Merchant</TableCell>
              <TableCell style={{ width: "14.77%" }}>Price</TableCell>
              <TableCell style={{ width: "19.25%" }}>Available/Limit</TableCell>
              <TableCell style={{ width: "10.27%" }}>Payment</TableCell>
              <TableCell style={{ width: "15.71%" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tradeList &&
              tradeList
                .sort((a, b) =>
                  new BigNumber(b.price).comparedTo(new BigNumber(a.price)),
                )
                .slice(
                  (page - 1) * rowsPerPage,
                  (page - 1) * rowsPerPage + rowsPerPage,
                )
                .map((row: IVWPost) => (
                  <TableRow
                    key={row.post_id}
                    sx={{
                      borderRadius: theme.borderRadius.main,
                    }}
                  >
                    <TableCell>{row.asset_name}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      {formatedBigNumber(row.price)} {row.currency}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body4" sx={{ fontWeight: "500" }}>
                        {`Available: ${numeral(
                          formatedBigNumber(row.token_amount),
                        ).format("0,0.000")} ${row.asset_name}`}
                      </Typography>
                      <br />
                      <Typography variant="body4" sx={{ fontWeight: "500" }}>
                        {`Limit: ${
                          row.minOrder &&
                          numeral(formatedBigNumber(row.minOrder)).format(
                            "0,0.000",
                          )
                        } - ${
                          row.maxOrder &&
                          numeral(formatedBigNumber(row.maxOrder)).format(
                            "0,0.000",
                          )
                        } `}
                        {row.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.payment_name}</TableCell>
                    <TableCell>
                      <Button
                        variant="table"
                        color="primary"
                        size="small"
                        onClick={() => {
                          if (userStore?.userId === row.created_by) {
                            enqueueSnackbar("You cannot trade with yourself.", {
                              variant: "warning",
                            });
                          } else if (isLogin) {
                            setSelectedPost(row);
                          } else {
                            enqueueSnackbar("Please sign in to continue.", {
                              variant: "warning",
                            });
                          }
                        }}
                      >
                        Trade
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        {tradeList && tradeList.length > rowsPerPage && (
          <TablePagination
            page={page}
            count={tradeList.length}
            pageLimit={rowsPerPage}
            onChange={handleChangePage}
          />
        )}
        {tradeList && tradeList.length === 0 && (
          <>
            <Typography
              variant="h3"
              color={theme.colors.noFound}
              mt="82.24px"
              textAlign="center"
            >
              There are no posts yet. Be the first to create one!
            </Typography>
            <Box width="fit-content" mt="59px" mb="69px" mx="auto">
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => {
                  navigate("/trade/create-post");
                }}
              >
                Create a new post
              </Button>
            </Box>
          </>
        )}
        {selectStore && (
          <Alert alertData={{ closeMethod: () => setSelectStore(null) }}>
            <Avatar
              {...stringAvatar(
                selectStore.firstname + " " + selectStore.lastname ?? "",
              )}
            ></Avatar>
            <Typography variant="h3" my="1rem">
              {selectStore.title}
            </Typography>
            <Grid container direction="row" mb="1.5rem" rowGap="0.5rem">
              <Grid item sm={6}>
                <Typography variant="body1">Firstname:</Typography>
              </Grid>
              <Grid item sm={6}>
                <Typography variant="body1">{selectStore.firstname}</Typography>
              </Grid>
              <Grid item sm={6}>
                <Typography variant="body1">Lastname:</Typography>
              </Grid>
              <Grid item sm={6}>
                <Typography variant="body1">{selectStore.lastname}</Typography>
              </Grid>
              <Grid item sm={6}>
                <Typography variant="body1">Phone number:</Typography>
              </Grid>
              <Grid item sm={6}>
                <Typography variant="body1">
                  {selectStore.phone_number}
                </Typography>
              </Grid>
            </Grid>
          </Alert>
        )}
      </>
    );
  }),
);
export default TradeTable;
